/**
 * Ägarens namn på ansökan — sparat en gång, inte slaget upp vid varje
 * rendering.
 *
 * Ansökningar bar bara ownedBy (när klienten råkade sätta det) och aldrig
 * ownerName. Vyerna gjorde ett uppslag per ägare, och saknades users-
 * dokumentet stod det "Okänd ägare". Nu skrivs ett snapshot när ansökan
 * skapas (trigger på servern) och i efterhand för gamla (backfill).
 *
 * Regeln för var namnet kommer ifrån är densamma i båda: users-dokumentet,
 * annars lägenhetens medlem — men bara om lägenheten har exakt en. Annars
 * ingen gissning.
 *
 * Speglas i functions/src/utils/ownerSnapshot.ts — functions är ett eget paket.
 */
import { hasAnyIdentity, memberDisplayName, type MemberLike } from './members';

export interface OwnerSnapshot {
  ownerName: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string | null;
  ownerSnapshotSource: 'user' | 'housing';
}

type UserLike = { firstName?: string | null; lastName?: string | null; email?: string | null } | null | undefined;

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Platshållaren som AddRentalModal SPARADE som namn (förnamn "Okänd", efternamn
 * "Ägare") när ingen ägare valts. Den är inget ägarnamn — räknas den som ett
 * hoppar ifyllningen över just de uthyrningar som saknar ägare.
 */
export function isPlaceholderOwnerName(name: unknown): boolean {
  return text(name).toLowerCase().replace(/\s+/g, ' ') === 'okänd ägare';
}

const realName = (first: unknown, last: unknown): boolean =>
  !!(text(first) && text(last)) && !isPlaceholderOwnerName(`${text(first)} ${text(last)}`);

/** Har ansökan redan ett användbart ägarnamn? */
export function hasOwnerSnapshot(app: {
  ownerName?: unknown;
  ownerFirstName?: unknown;
  ownerLastName?: unknown;
  owners?: Array<{ firstName?: unknown; lastName?: unknown }> | null;
}): boolean {
  if (text(app.ownerName) && !isPlaceholderOwnerName(app.ownerName)) return true;
  if (realName(app.ownerFirstName, app.ownerLastName)) return true;
  const first = app.owners && app.owners[0];
  return !!(first && realName(first.firstName, first.lastName));
}

export function resolveOwnerSnapshot(input: { user: UserLike; housingMembers: MemberLike[] | null | undefined }): OwnerSnapshot | null {
  const { user, housingMembers } = input;
  if (user) {
    const firstName = text(user.firstName);
    const lastName = text(user.lastName);
    const email = text(user.email);
    const ownerName = `${firstName} ${lastName}`.trim() || email;
    if (ownerName) {
      return { ownerName, ownerFirstName: firstName, ownerLastName: lastName, ownerEmail: email || null, ownerSnapshotSource: 'user' };
    }
  }
  if (Array.isArray(housingMembers) && housingMembers.length === 1 && hasAnyIdentity(housingMembers[0])) {
    const m = housingMembers[0]!;
    const ownerName = memberDisplayName(m);
    if (ownerName) {
      return {
        ownerName,
        ownerFirstName: text(m.firstName),
        ownerLastName: text(m.lastName),
        ownerEmail: text(m.email) || null,
        ownerSnapshotSource: 'housing',
      };
    }
  }
  return null;
}
